import { useCallback, useReducer, useRef, useState } from 'react';
import { Room, RoomEvent, ParticipantEvent, Track } from 'livekit-client';
import Agent from 'services/api/Agent';
import { normalizeMeteringTo01 } from 'utils/audioMetering';
import { reduceCall, initialCall } from './callMachine';

const AGENT_STATE_ATTR = 'lk.agent.state';

// Stable id for the single temporary streaming bubble shown while the agent
// speaks. The real persisted message arrives shortly after via the `newMessage`
// socket with its own real `_id`; this temp bubble is removed once the segment
// is final so we never keep a permanent duplicate.

// One shared AudioContext for the whole module so we don't leak contexts across
// repeated calls. It is suspended (never closed) on teardown so it can be reused.
let sharedAudioContext = null;
function getOrCreateAudioContext() {
  if (!sharedAudioContext) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    sharedAudioContext = new Ctx();
  }
  return sharedAudioContext;
}

const outputSwitchingSupported = () =>
  typeof HTMLMediaElement !== 'undefined' && 'sinkId' in HTMLMediaElement.prototype;

const enumerateKind = async kind => {
  try {
    return await Room.getLocalDevices(kind);
  } catch {
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.filter(d => d.kind === kind);
  }
};

const resolveActiveDeviceIds = (room, inputs, outputs, prev) => ({
  activeInput:
    room?.getActiveDevice?.('audioinput') ?? prev.activeInput ?? inputs[0]?.deviceId ?? null,
  activeOutput: outputSwitchingSupported()
    ? (room?.getActiveDevice?.('audiooutput') ?? prev.activeOutput ?? outputs[0]?.deviceId ?? null)
    : null,
});

export default function useVoiceCall({ agentId, appUserId }) {
  const [state, dispatch] = useReducer(reduceCall, initialCall);
  const [muted, setMuted] = useState(false);
  // The user's live speech-to-text, shown in the composer placeholder while they
  // speak. The agent's reply and the user's final message arrive as normal
  // persisted messages over the socket (reliable, no client-side reconciliation).
  const [userLiveText, setUserLiveText] = useState('');

  const teardownStreamBubble = useCallback(() => {
    setUserLiveText('');
  }, []);
  const [devices, setDevices] = useState({
    inputs: [],
    outputs: [],
    activeInput: null,
    activeOutput: null,
  });

  const roomRef = useRef(null);
  const audioElRef = useRef(null);

  // Web Audio metering graph.
  const userAnalyserRef = useRef(null);
  const agentAnalyserRef = useRef(null);
  const userSourceRef = useRef(null);
  const agentSourceRef = useRef(null);
  // Reusable byte buffers (one per analyser) so getLevels allocates nothing.
  const userBufRef = useRef(null);
  const agentBufRef = useRef(null);

  const buildAnalyser = useCallback((mediaStreamTrack, analyserRef, sourceRef, bufRef) => {
    if (!mediaStreamTrack) return;
    const ctx = getOrCreateAudioContext();
    // Tear down any previous graph for this side before rebuilding.
    try {
      sourceRef.current?.disconnect();
    } catch {
      /* noop */
    }
    const source = ctx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.6;
    source.connect(analyser);
    sourceRef.current = source;
    analyserRef.current = analyser;
    bufRef.current = new Uint8Array(analyser.fftSize);
  }, []);

  const attachLocalAnalyser = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const pub = room.localParticipant.getTrackPublications().find(p => p.kind === Track.Kind.Audio);
    const mst = pub?.track?.mediaStreamTrack;
    if (mst) buildAnalyser(mst, userAnalyserRef, userSourceRef, userBufRef);
  }, [buildAnalyser]);

  const readAttributes = useCallback(attrs => {
    if (attrs && AGENT_STATE_ATTR in attrs) {
      dispatch({ type: 'AGENT_STATE', value: attrs[AGENT_STATE_ATTR] });
    }
  }, []);

  const loadDeviceLists = useCallback(async () => {
    const inputDevices = await enumerateKind('audioinput');
    const inputs = inputDevices.map(d => ({ deviceId: d.deviceId, label: d.label }));

    let outputs = [];
    if (outputSwitchingSupported()) {
      const outputDevices = await enumerateKind('audiooutput');
      outputs = outputDevices.map(d => ({ deviceId: d.deviceId, label: d.label }));
    }

    return { inputs, outputs };
  }, []);

  const refreshDevices = useCallback(async () => {
    const room = roomRef.current;
    const { inputs, outputs } = await loadDeviceLists();
    setDevices(prev => ({
      inputs,
      outputs,
      ...resolveActiveDeviceIds(room, inputs, outputs, prev),
    }));
  }, [loadDeviceLists]);

  const ensureActiveDevices = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;

    const { inputs, outputs } = await loadDeviceLists();
    let nextDevices;
    setDevices(prev => {
      nextDevices = { inputs, outputs, ...resolveActiveDeviceIds(room, inputs, outputs, prev) };
      return nextDevices;
    });

    const { activeInput, activeOutput } = nextDevices;
    if (activeInput) {
      await room.switchActiveDevice('audioinput', activeInput);
    }
    if (activeOutput && outputSwitchingSupported()) {
      await room.switchActiveDevice('audiooutput', activeOutput);
    }
    if (activeInput) attachLocalAnalyser();
  }, [attachLocalAnalyser, loadDeviceLists]);

  const switchDevice = useCallback(
    async (kind, deviceId) => {
      const room = roomRef.current;
      if (!room) return;
      if (kind === 'audiooutput' && !outputSwitchingSupported()) return;
      await room.switchActiveDevice(kind, deviceId);
      setDevices(prev => ({
        ...prev,
        ...(kind === 'audioinput' ? { activeInput: deviceId } : {}),
        ...(kind === 'audiooutput' ? { activeOutput: deviceId } : {}),
      }));
      // Local mic device changed: rebuild the analyser on the new track.
      if (kind === 'audioinput') attachLocalAnalyser();
    },
    [attachLocalAnalyser],
  );

  const start = useCallback(async () => {
    try {
      dispatch({ type: 'CONNECT' });
      // Resume the shared AudioContext from within the user gesture.
      const ctx = getOrCreateAudioContext();
      if (ctx.state === 'suspended') await ctx.resume();

      const { url, token } = await Agent.voiceToken(agentId, appUserId);
      const room = new Room({ adaptiveStream: false, dynacast: false });
      roomRef.current = room;

      room.on(RoomEvent.Connected, () => {
        dispatch({ type: 'CONNECTED' });
        refreshDevices();
      });
      room.on(RoomEvent.Disconnected, () => dispatch({ type: 'END' }));

      room.on(RoomEvent.TrackSubscribed, track => {
        if (track.kind === Track.Kind.Audio) {
          const el = track.attach();
          audioElRef.current = el;
          document.body.appendChild(el);
          // Remote (agent) audio analyser.
          buildAnalyser(track.mediaStreamTrack, agentAnalyserRef, agentSourceRef, agentBufRef);
        }
      });
      room.on(RoomEvent.TrackUnsubscribed, track => track.detach());

      room.on(RoomEvent.LocalTrackPublished, pub => {
        if (pub.kind === Track.Kind.Audio && pub.track?.mediaStreamTrack) {
          buildAnalyser(pub.track.mediaStreamTrack, userAnalyserRef, userSourceRef, userBufRef);
        }
      });

      room.on(RoomEvent.MediaDevicesChanged, () => refreshDevices());

      // Agent phase via participant attributes.
      room.on(RoomEvent.ParticipantAttributesChanged, (changed, participant) => {
        if (participant !== room.localParticipant) readAttributes(participant.attributes);
      });
      room.on(RoomEvent.ParticipantConnected, participant =>
        readAttributes(participant.attributes),
      );

      room.on(RoomEvent.ActiveSpeakersChanged, speakers => {
        const remoteSpeaking = speakers.some(p => p !== room.localParticipant);
        dispatch({ type: 'AGENT_SPEAKING', value: remoteSpeaking });
      });
      room.localParticipant.on(ParticipantEvent.IsSpeakingChanged, v =>
        dispatch({ type: 'USER_SPEAKING', value: v }),
      );
      room.localParticipant.on(ParticipantEvent.TrackMuted, () => setMuted(true));
      room.localParticipant.on(ParticipantEvent.TrackUnmuted, () => setMuted(false));

      room.registerTextStreamHandler('lk.transcription', async (reader, info) => {
        const id = reader.info.attributes['lk.segment_id'] || reader.info.id;
        const isLocal = info.identity === room.localParticipant.identity;
        try {
          let acc = '';
          // The LiveKit reader is async-iterable; each chunk is a delta.

          for await (const chunk of reader) {
            acc += chunk;
            // The user's live words show in the composer placeholder as they speak.
            if (isLocal) setUserLiveText(acc);
          }
          const final = reader.info.attributes['lk.transcription_final'] === 'true';
          if (isLocal) {
            // The finalized user turn appears as a normal persisted message.
            if (final) setUserLiveText('');
          } else {
            // Agent reply appears via its persisted message; keep legacy transcript.
            dispatch({ type: 'TRANSCRIPT', id: `${info.identity}:${id}`, text: acc, final });
          }
        } catch {
          // Never throw inside the handler (would break transcription).
          if (isLocal) setUserLiveText('');
        }
      });

      await room.connect(url, token);
      await room.startAudio(); // satisfy autoplay (called from the user gesture)
      await room.localParticipant.setMicrophoneEnabled(true);
      setMuted(!room.localParticipant.isMicrophoneEnabled);
      await ensureActiveDevices();
      // Pick up the agent participant's attributes if it joined before our listener.
      room.remoteParticipants?.forEach(p => readAttributes(p.attributes));

      if (navigator.mediaDevices) {
        navigator.mediaDevices.addEventListener?.('devicechange', refreshDevices);
      }
    } catch (err) {
      // Drop any half-written streaming bubble so a failed call never strands a
      // permanent duplicate in the conversation.
      teardownStreamBubble();
      dispatch({ type: 'ERROR', error: err?.message || 'connection failed' });
    }
  }, [
    agentId,
    appUserId,
    buildAnalyser,
    readAttributes,
    ensureActiveDevices,
    teardownStreamBubble,
    refreshDevices,
  ]);

  const toggleMute = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const enabled = room.localParticipant.isMicrophoneEnabled;
    await room.localParticipant.setMicrophoneEnabled(!enabled);
    setMuted(enabled); // was enabled -> now muted
  }, []);

  const levelFromAnalyser = useCallback((analyser, buf) => {
    if (!analyser || !buf) return 0;
    analyser.getByteTimeDomainData(buf);
    let sumSq = 0;
    for (let i = 0; i < buf.length; i += 1) {
      const v = (buf[i] - 128) / 128; // center & normalize to -1..1
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / buf.length);
    if (rms <= 0) return 0;
    const db = 20 * Math.log10(rms);
    return normalizeMeteringTo01(db);
  }, []);

  const getLevels = useCallback(
    () => ({
      user: levelFromAnalyser(userAnalyserRef.current, userBufRef.current),
      agent: levelFromAnalyser(agentAnalyserRef.current, agentBufRef.current),
    }),
    [levelFromAnalyser],
  );

  const end = useCallback(async () => {
    try {
      await roomRef.current?.disconnect();
    } finally {
      // Remove the temp streaming bubble (and cancel any pending removal timer)
      // so ending mid-stream never leaves a permanent duplicate behind.
      teardownStreamBubble();

      audioElRef.current?.remove();
      audioElRef.current = null;

      // Tear down Web Audio graph.
      [userSourceRef, agentSourceRef].forEach(ref => {
        try {
          ref.current?.disconnect();
        } catch {
          /* noop */
        }
        ref.current = null;
      });
      [userAnalyserRef, agentAnalyserRef].forEach(ref => {
        try {
          ref.current?.disconnect();
        } catch {
          /* noop */
        }
        ref.current = null;
      });
      userBufRef.current = null;
      agentBufRef.current = null;

      // Suspend (do NOT close) so the context can be reused on the next call.
      if (sharedAudioContext && sharedAudioContext.state === 'running') {
        sharedAudioContext.suspend().catch(() => {});
      }

      if (navigator.mediaDevices) {
        navigator.mediaDevices.removeEventListener?.('devicechange', refreshDevices);
      }

      roomRef.current = null;
      setUserLiveText('');
      dispatch({ type: 'END' });
    }
  }, [refreshDevices, teardownStreamBubble]);

  const phase = (() => {
    if (state.status === 'connecting') return 'connecting';
    if (state.phase) return state.phase;
    return state.agentSpeaking ? 'speaking' : 'listening';
  })();

  return {
    state,
    phase,
    muted,
    userLiveText,
    start,
    toggleMute,
    end,
    getLevels,
    devices,
    switchDevice,
    refreshDevices,
  };
}
